import React from 'react';
import { Button } from '../ui/button';
import FundingMatchFitTierBadge from './FundingMatchFitTierBadge';
import FundingSequenceItemStatusBadge from './FundingSequenceItemStatusBadge';
import FundingSequenceReorderControls from './FundingSequenceReorderControls';

const formatCurrency = (value) => {
  if (value == null || value === '') return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
};

const formatFundingSpeed = (min, max) => {
  if (min == null && max == null) return '—';
  if (min != null && max != null) return `${min}-${max} days`;
  return `${min ?? max} days`;
};

const FundingSequenceItemCard = ({ item, onStatusChange, onMovePhase, onEditNotes, onCreateSubmission, creatingSubmission, disabled }) => {
  if (!item) return null;

  const rangeLabel = item.expectedAmountMin != null || item.expectedAmountMax != null
    ? `${formatCurrency(item.expectedAmountMin)} – ${formatCurrency(item.expectedAmountMax)}`
    : '—';

  return (
    <div className="rounded-2xl border border-[var(--fg-line)] bg-white p-4 dark:bg-slate-950/20">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-[var(--fg-text)]">{item.lenderName}</div>
          <div className="mt-1 text-sm text-[var(--fg-muted)]">{item.lenderType} · {item.productType}</div>
        </div>
        <div className="text-right">
          {item.matchScore != null ? (
            <div className="text-lg font-bold text-[var(--fg-accent)]">{item.matchScore}%</div>
          ) : null}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {item.fitTier ? <FundingMatchFitTierBadge tier={item.fitTier} /> : null}
        <FundingSequenceItemStatusBadge status={item.status} />
        <span className="rounded-full bg-[var(--fg-panel-soft)] px-2.5 py-1 text-xs text-[var(--fg-muted)]">
          Est. {rangeLabel}
        </span>
        <span className="rounded-full bg-[var(--fg-panel-soft)] px-2.5 py-1 text-xs text-[var(--fg-muted)]">
          Speed {formatFundingSpeed(item.fundingSpeedDaysMin, item.fundingSpeedDaysMax)}
        </span>
      </div>

      {item.missingDocuments?.length > 0 ? (
        <div className="mt-3 text-xs text-amber-700 dark:text-amber-300">
          Missing: {item.missingDocuments.join(', ')}
        </div>
      ) : null}

      {item.nextAction ? (
        <div className="mt-3 rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-3 py-2 text-sm">
          <span className="font-semibold text-[var(--fg-accent)]">Next action: </span>
          {item.nextAction}
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        <FundingSequenceReorderControls item={item} onMovePhase={onMovePhase} disabled={disabled} />
        {['Ready', 'Applied', 'Approved', 'Declined', 'Funded', 'Skipped'].map((status) => (
          <Button
            key={status}
            size="sm"
            variant="outline"
            className="rounded-xl"
            disabled={disabled || item.status === status}
            onClick={() => onStatusChange(item, status)}
          >
            {status === 'Applied' ? 'Mark Applied' : status === 'Approved' ? 'Mark Approved' : status === 'Declined' ? 'Mark Declined' : status === 'Funded' ? 'Mark Funded' : status}
          </Button>
        ))}
        <Button size="sm" variant="ghost" className="rounded-xl" onClick={() => onEditNotes(item)} disabled={disabled}>
          Edit notes
        </Button>
        {onCreateSubmission ? (
          <Button
            size="sm"
            variant="outline"
            className="rounded-xl"
            onClick={() => onCreateSubmission(item)}
            disabled={disabled || creatingSubmission}
          >
            {creatingSubmission ? 'Creating...' : 'Create Submission'}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default FundingSequenceItemCard;
